import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { uid, formatMontant, exportCSV } from '../utils/helpers';
import { findBestMatch, computeFingerprint } from './bankUtils';

const pdfjsLib = window.pdfjsLib;
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js';


// ─── Sélecteur adhérent avec autocomplétion (remplace le <select> fixe) ─────
function AdherentPicker({ value, adherents, onChange, placeholder = '🔍 Rechercher…' }) {
  const [inputVal,   setInputVal]   = useState('');
  const [open,       setOpen]       = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef  = useRef();
  const inputRef = useRef();

  const selected    = value ? adherents.find(a => a.id === value) : null;
  const displayName = selected ? `${selected.nom} ${selected.prenom}` : '';

  const suggestions = useMemo(() => {
    const norm = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();
    const q = norm(inputVal);
    const list = q
      ? adherents.filter(a => {
          const full = norm(`${a.nom} ${a.prenom}`);
          const rev  = norm(`${a.prenom} ${a.nom}`);
          return full.includes(q) || rev.includes(q);
        })
      : adherents;
    return list.slice(0, 10);
  }, [adherents, inputVal]);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectAdherent = (id) => { onChange(id); setInputVal(''); setOpen(false); };

  const handleKeyDown = (e) => {
    if (!open) { if (e.key === 'ArrowDown') setOpen(true); return; }
    if (e.key === 'ArrowDown') { setHighlighted(h => Math.min(h+1, suggestions.length-1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp')  { setHighlighted(h => Math.max(h-1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter' && suggestions[highlighted]) { selectAdherent(suggestions[highlighted].id); e.preventDefault(); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position:'relative', flex:1, minWidth:160 }}>
      <input ref={inputRef} className="form-control"
        style={{ fontSize:12, padding:'4px 8px', width:'100%' }}
        placeholder={placeholder}
        value={open || !selected ? inputVal : displayName}
        onFocus={() => { setOpen(true); setInputVal(''); setHighlighted(0); }}
        onChange={e => { setInputVal(e.target.value); onChange(null); setHighlighted(0); }}
        onKeyDown={handleKeyDown}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:2000,
          background:'#fff', border:'1px solid #e5e7eb', borderRadius:6,
          boxShadow:'0 4px 16px rgba(0,0,0,0.15)', maxHeight:200, overflowY:'auto',
        }}>
          {suggestions.map((a, i) => (
            <div key={a.id}
              onMouseDown={e => { e.preventDefault(); selectAdherent(a.id); }}
              style={{
                padding:'7px 10px', fontSize:12, cursor:'pointer',
                background: i === highlighted ? '#eff6ff' : '#fff',
                borderBottom:'1px solid #f3f4f6',
              }}>
              {a.nom} {a.prenom}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

// ─── Parse BFC OI relevé : virements (crédit) + impayés (débit) ───────────────
// Structure BFC OI : [date] [libellé] [date_valeur] [débit_ou_crédit] [SOLDE_INTERMEDIAIRE]
// Le DERNIER montant = solde intermédiaire → toujours ignoré
// L'AVANT-DERNIER montant = montant réel de l'opération
function parseBFCLines(fullText) {
  const lines = fullText.split('\n');
  const credits = [];
  const debits  = [];
  const monthCounts = {};

  const rDate   = /^(\d{2})\/(\d{2})(?:\/(\d{2,4}))?/;
  // VIRT INSTANTANE DE = virement instantané → traiter comme VIRT DE (crédit)
  const rVir    = /\b(?:VIRT?\s+INSTANTANE\s+DE\b|VIRT?\s+DE\b|VIREMENT\s*RE[CÇ]U|VIR\s+INST|VIR\s*RECU\b)/i;
  const rImpaye = /\b(?:IMPAYE\s*PREL|IMP\s+PREL|REJET\s+ORDRE\s+TIRE|REJET\s+PRELEVEMENT|PROVISION\s+INSUF)/i;
  // VIR FAV = reversement fédéral ; FRAIS IMPAYE = pénalité BFC (pas un impayé adhérent)
  const rSkip   = /\b(?:PRLV\b|PRELEVEMENT\s+EMIS|RETRAIT|VIR\s*EMIS|VIR\s+FAV\b|FRAIS\s+IMPAYE\b|BFCNET|ABO\s+BFC|FRAIS\s+TENUE|FACTUR\s+CONNEXION|COMM\s*PREL|EXT\s+FRAIS)\b/i;

  let lastDate = '';

  const extractActualAmount = (line) => {
    // CRITIQUE : supprimer les tokens date JJ/MM avant d'extraire les montants
    // Sans ça : "02/01 12,00" → regex voit "01 12,00" → parseFloat("0112.00") = 112 ❌
    const stripped = line.replace(/\b\d{2}\/\d{2}\b/g, ' ');
    const all = (stripped.match(/(\d[\d ]*[,\.]\d{2})/g) || [])
      .map(m => parseFloat(m.replace(/\s/g,'').replace(',','.')))
      .filter(n => n > 0.01 && n < 9999999);
    if (!all.length) return null;
    // BFC OI : dernier = solde intermédiaire (ignorer) ; avant-dernier = montant réel
    return all.length >= 2 ? all[all.length - 2] : all[0];
  };

  // Nettoyer le nom extrait : supprimer montants, dates, titres civils, "DE" résiduel
  const cleanName = (s) =>
    s.replace(/(\d[\d ]*[,\.]\d{2})/g, '')
     .replace(/\d{2}\/\d{2}(\/\d{2,4})?/g, '')
     .replace(/\bDATE\s*\d*/gi, '')
     .replace(/\b(?:MME|MLE|MLLE|MR|MADAME|MONSIEUR|NOTPROVIDED)\b/gi, '')
     .replace(/^\s*DE\s+/i, '')
     .replace(/\s+/g, ' ').trim().slice(0, 45);

  const dedupe = (arr) => {
    const seen = new Set();
    return arr.filter(v => {
      const k = `${v.date}|${v.nomBrut}|${v.montant}`;
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });
  };

  // ── Fusionner les lignes de continuation PDF (libellés longs sur 2 lignes) ──
  // Une ligne de continuation ne commence pas par DD/MM
  const mergedLines = [];
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) continue;
    if (/^\d{2}\/\d{2}/.test(l)) {
      mergedLines.push(l);
    } else if (mergedLines.length > 0) {
      mergedLines[mergedLines.length - 1] += ' ' + l;
    }
  }

  for (const rawLine of mergedLines) {
    const line = rawLine.trim();
    if (!line || line.length < 5) continue;

    const dm = line.match(rDate);
    if (dm) {
      lastDate = `${dm[1]}/${dm[2]}`;
      const mKey = dm[2];
      monthCounts[mKey] = (monthCounts[mKey] || 0) + 1;
    }

    if (rSkip.test(line)) continue;

    const dateStr = dm ? `${dm[1]}/${dm[2]}` : lastDate;
    const actualAmount = extractActualAmount(line);
    if (!actualAmount || actualAmount > 99999) continue;

    if (rVir.test(line)) {
      const virIdx = line.search(rVir);
      const nomBrut = cleanName(line.substring(virIdx).replace(rVir, ''));
      if (nomBrut.length < 2) continue;
      credits.push({ id: uid(), date: dateStr, nomBrut, montant: actualAmount,
        ligneOriginale: line, status: 'none', matchedAdherentId: null, matchScore: 999,
        pointe: false, manualAdherentId: null, validated: false, validatedAt: null });
    } else if (rImpaye.test(line)) {
      const nomBrut = cleanName(line.replace(rImpaye, '').replace(rDate, ''));
      debits.push({ id: uid(), date: dateStr, nomBrut, montant: actualAmount,
        ligneOriginale: line, status: 'none', matchedAdherentId: null, matchScore: 999,
        confirmed: false, ignored: false, ignoreNote: '', manualAdherentId: null });
    }
  }

  // Déterminer le mois le plus représenté dans le relevé
  let detectedMonth = null, maxCount = 0;
  for (const [m, count] of Object.entries(monthCounts)) {
    if (count > maxCount) { maxCount = count; detectedMonth = parseInt(m, 10); }
  }

  return { credits: dedupe(credits), debits: dedupe(debits), detectedMonth };
}


// ─── History (localStorage) ───────────────────────────────────────────────────
const HIST_KEY      = 'sdassp_pointage_releve_v1';
const SYNCED_FP_KEY = 'sdassp_synced_fps_v1';
const loadSyncedFps = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SYNCED_FP_KEY)) || []); }
  catch { return new Set(); }
};
const saveSyncedFps = fps => {
  try { localStorage.setItem(SYNCED_FP_KEY, JSON.stringify([...fps].slice(0, 2000))); }
  catch {}
};
const loadHist = () => { try { return JSON.parse(localStorage.getItem(HIST_KEY)) || []; } catch { return []; } };
const saveHist = h => { try { localStorage.setItem(HIST_KEY, JSON.stringify(h.slice(0, 20))); } catch {} };

// ─── Component ────────────────────────────────────────────────────────────────
const EMPTY_CREATE = { civilite:'M.', prenom:'', nom:'', etablissementId:'', montant:'', modePaiement:'Virement', periodicite:'Mensuelle', moisEcheance:'' };

export default function PointageReleve({
  adherents, cotisations, setCotisation, selectedYear, currentUsername, addJournal,
  saveAdherent, etablissements,
}) {
  const [virements,     setVirements]     = useState([]);   // crédits
  const [impayes,       setImpayes]       = useState([]);   // débits / rejets
  const [detectedMonth, setDetectedMonth] = useState(null);
  const [processing,    setProcessing]    = useState(false);
  const [filterMode,    setFilterMode]    = useState('all');
  const [history,       setHistory]       = useState(loadHist);
  const [error,         setError]         = useState('');
  const [isDrag,        setIsDrag]        = useState(false);
  const [validationMsg, setValidationMsg] = useState('');
  const [createModal, setCreateModal]     = useState(null); // { virementId, form }
  const fileRef = useRef();

  const relevéMonth = detectedMonth || (new Date().getMonth() + 1);
  const motifBase   = `Relevé BFC ${String(relevéMonth).padStart(2,'0')}/${selectedYear}`;

  // ── Import + parse PDF ──
  const processPDF = useCallback(async (file) => {
    setProcessing(true); setError(''); setVirements([]); setImpayes([]);
    setFilterMode('all'); setValidationMsg('');
    try {
      const ab = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(ab) }).promise;
      let fullText = '';
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const tc   = await page.getTextContent();
        const rows = [];
        for (const item of tc.items) {
          if (!item.str.trim()) continue;
          const y = Math.round(item.transform[5] / 4) * 4;
          const r = rows.find(r => r.y === y);
          if (r) r.parts.push({ x: item.transform[4], text: item.str });
          else rows.push({ y, parts: [{ x: item.transform[4], text: item.str }] });
        }
        rows.sort((a, b) => b.y - a.y);
        for (const row of rows) {
          row.parts.sort((a, b) => a.x - b.x);
          fullText += row.parts.map(p => p.text).join(' ') + '\n';
        }
      }

      const { credits, debits, detectedMonth: dm } = parseBFCLines(fullText);

      const syncedFps = loadSyncedFps();
      const matchedCredits = credits.map(v => {
        const fp  = computeFingerprint(v.date, v.montant, 'credit', v.nomBrut);
        const { adherentId, score, reasons, status: raw } = findBestMatch(v.nomBrut, adherents, v.montant);
        const status = raw === 'recognized' ? 'auto' : raw === 'review' ? 'approx' : 'none';
        const alreadySynced = syncedFps.has(fp);
        return {
          ...v, status, matchedAdherentId: adherentId,
          matchScore: score, matchReasons: reasons || [],
          fingerprint: fp,
          pointe: status === 'auto' && !alreadySynced,
          validated: alreadySynced, validatedAt: alreadySynced ? '(relevé précédent)' : null,
        };
      });
      const matchedDebits = debits.map(v => {
        const fp  = computeFingerprint(v.date, v.montant, 'debit', v.nomBrut);
        const { adherentId, score, reasons, status: raw } = findBestMatch(v.nomBrut, adherents, v.montant);
        const status = raw === 'recognized' ? 'auto' : raw === 'review' ? 'approx' : 'none';
        return { ...v, status, matchedAdherentId: adherentId, matchScore: score, matchReasons: reasons || [], fingerprint: fp };
      });

      setVirements(matchedCredits);
      setImpayes(matchedDebits);
      setDetectedMonth(dm);

      const moisLabel = dm ? MOIS_FR[dm - 1] : '?';
      const entry = {
        id: uid(), date: new Date().toISOString(), fichier: file.name,
        mois: moisLabel, total: matchedCredits.length, impayes: matchedDebits.length,
        auto: matchedCredits.filter(v => v.status === 'auto').length,
        montantTotal: matchedCredits.reduce((s, v) => s + v.montant, 0),
      };
      const newHist = [entry, ...history].slice(0, 20);
      setHistory(newHist); saveHist(newHist);

      if (!matchedCredits.length && !matchedDebits.length)
        setError('Aucune opération détectée. Vérifiez que le fichier est un relevé BFC OI ("VIRT DE", "IMPAYE PREL").');
    } catch (e) {
      setError(`Erreur lecture PDF : ${e.message}`);
    } finally {
      setProcessing(false);
    }
  }, [adherents, history]);

  const handleFileChange = e => {
    const f = e.target.files[0];
    if (f) processPDF(f);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault(); setIsDrag(false);
    const f = e.dataTransfer.files[0];
    if (f?.type === 'application/pdf') processPDF(f);
    else if (f) setError('Veuillez sélectionner un fichier PDF.');
  };

  // ── Virements (crédits) ──
  const togglePointe = (id, val) =>
    setVirements(vs => vs.map(v => v.id === id ? { ...v, pointe: val } : v));
  const toggleAll = val =>
    setVirements(vs => vs.map(v =>
      (v.manualAdherentId || v.matchedAdherentId) && !v.validated ? { ...v, pointe: val } : v));
  const setManualAdherent = (id, adhId) =>
    setVirements(vs => vs.map(v =>
      v.id !== id ? v : { ...v, manualAdherentId: adhId || null, status: adhId ? 'approx' : 'none', pointe: !!adhId }));

  const openCreateModal = (v, isImpaye = false) => {
    const parts = v.nomBrut.trim().split(/\s+/);
    const guessNom    = parts[0] || '';
    const guessPrenom = parts.slice(1).join(' ') || '';
    setCreateModal({ virementId: v.id, isImpaye, isValidated: !!v.validated, form: { ...EMPTY_CREATE, nom: guessNom, prenom: guessPrenom, montant: String(v.montant) } });
  };

  const handleCreateAdherent = () => {
    if (!createModal || !createModal.form.nom.trim()) return;
    const f = createModal.form;
    const newId = uid();
    const year = new Date().getFullYear();
    const seq = String((adherents || []).length + 1).padStart(3, '0');
    const newAdh = {
      id: newId, prenom: f.prenom.trim(), nom: f.nom.trim().toUpperCase(),
      genre: f.civilite === 'Mme' ? 'Madame' : 'Monsieur',
      numero: `FO-${year}-${seq}`,
      statut: 'Actif', createdAt: new Date().toISOString(),
      etablissementId: f.etablissementId || '',
      metier: '', contrat: 'CDI', convention: 'CCN 51', coefficient: '',
      dateAdhesion: new Date().toISOString().slice(0,10),
      modePaiement: f.modePaiement || 'Virement',
      periodicite: f.periodicite || 'Mensuelle',
      moisEcheance: f.moisEcheance || '',
      montant: parseFloat(f.montant) || 0,
      rib: '', bic: '', rum: '', telephone: '', email: '', adresse: '', notes: '',
      documents: [],
    };
    saveAdherent && saveAdherent(newAdh);
    addJournal && addJournal('adhesion', `Adhérent créé depuis relevé : ${newAdh.prenom} ${newAdh.nom}`);
    if (createModal.isImpaye) {
      setImpayeAdherent(createModal.virementId, newId);
    } else if (createModal.isValidated) {
      // Ligne déjà validée : transférer la cotisation au nouvel adhérent
      const v = virements.find(x => x.id === createModal.virementId);
      if (v) changeValidatedAdherent(v, newId);
    } else {
      setManualAdherent(createModal.virementId, newId);
    }
    setCreateModal(null);
  };
  const cancelValidation = (v) => {
    const adhId = v.manualAdherentId || v.matchedAdherentId;
    if (adhId) setCotisation(selectedYear, adhId, relevéMonth, 'non_pointe', '');
    setVirements(vs => vs.map(x => x.id === v.id ? { ...x, validated: false, pointe: false, validatedAt: null } : x));
  };

  // Changer l'adhérent sur une ligne déjà validée : annule l'ancienne cotisation, crée la nouvelle
  const changeValidatedAdherent = (v, newAdhId) => {
    if (!newAdhId) return;
    const oldAdhId = v.manualAdherentId || v.matchedAdherentId;
    if (oldAdhId) setCotisation(selectedYear, oldAdhId, relevéMonth, 'non_pointe', '');
    setCotisation(selectedYear, newAdhId, relevéMonth, 'paye', motifBase);
    addJournal && addJournal('cotisation', `Correction affectation relevé : ${relevéMonth}/${selectedYear}`);
    setVirements(vs => vs.map(x =>
      x.id !== v.id ? x : { ...x, manualAdherentId: newAdhId, status: 'approx', validated: true }
    ));
  };

  // ── Impayes (débits / rejets SEPA) ──
  const setImpayeAdherent = (id, adhId) =>
    setImpayes(vs => vs.map(v =>
      v.id !== id ? v : { ...v, manualAdherentId: adhId || null, status: adhId ? 'approx' : 'none' }));
  const confirmImpaye = (imp) => {
    const adhId = imp.manualAdherentId || imp.matchedAdherentId;
    if (!adhId) return;
    setCotisation(selectedYear, adhId, relevéMonth, 'defaut_provision', `Rejet SEPA — ${motifBase}`);
    setImpayes(vs => vs.map(v => v.id === imp.id ? { ...v, confirmed: true } : v));
    addJournal && addJournal('cotisation', `Impayé confirmé — ${imp.nomBrut} — ${motifBase}`);
  };
  const ignoreImpaye = (id, note) =>
    setImpayes(vs => vs.map(v => v.id === id ? { ...v, ignored: true, ignoreNote: note } : v));

  // ── Valider pointage cotisations ──
  const handleValidate = () => {
    let created = 0, skipped = 0;
    const now = new Date().toISOString();
    const fps = loadSyncedFps();
    virements.forEach(v => {
      if (!v.pointe || v.validated) return;
      const adhId = v.manualAdherentId || v.matchedAdherentId;
      if (!adhId) return;
      if (v.fingerprint && fps.has(v.fingerprint)) { skipped++; return; }
      setCotisation(selectedYear, adhId, relevéMonth, 'paye', motifBase);
      if (v.fingerprint) fps.add(v.fingerprint);
      created++;
    });
    saveSyncedFps(fps);
    addJournal && addJournal('cotisation',
      `Pointage ${motifBase} : ${created} créée(s), ${skipped} doublon(s) ignoré(s) — ${currentUsername || 'admin'}`);
    const parts = [];
    if (created) parts.push(`${created} cotisation(s) créée(s)`);
    if (skipped) parts.push(`${skipped} doublon(s) ignoré(s)`);
    setValidationMsg(`✅ ${parts.join(' · ')} — ${MOIS_FR[relevéMonth - 1]} ${selectedYear}`);
    setVirements(vs => vs.map(v => {
      if (!v.pointe || v.validated || !(v.manualAdherentId || v.matchedAdherentId)) return v;
      return { ...v, validated: true, validatedAt: now };
    }));
  };

  // ── Export CSV ──
  const handleExport = () => {
    const rows = [['Date', 'Nom relevé', 'Montant (€)', 'Adhérent', 'Statut', 'Pointé']];
    virements.forEach(v => {
      const adh = adherents.find(a => a.id === (v.manualAdherentId || v.matchedAdherentId));
      rows.push([
        v.date, v.nomBrut, v.montant,
        adh ? `${adh.prenom} ${adh.nom}` : '—',
        v.status === 'auto' ? 'Reconnu' : v.status === 'approx' ? 'Approx.' : 'Non reconnu',
        v.pointe ? 'Oui' : 'Non',
      ]);
    });
    exportCSV(rows, `pointage_releve_${selectedYear}.csv`);
  };

  // ── KPI ──
  const kpi = useMemo(() => ({
    total:        virements.length,
    auto:         virements.filter(v => v.status === 'auto').length,
    approx:       virements.filter(v => v.status === 'approx').length,
    none:         virements.filter(v => v.status === 'none').length,
    pointes:      virements.filter(v => v.pointe && !v.validated).length,
    validated:    virements.filter(v => v.validated).length,
    totalMontant: virements.reduce((s, v) => s + v.montant, 0),
    impayes:      impayes.filter(v => !v.ignored).length,
    impayesConfirmed: impayes.filter(v => v.confirmed).length,
    impayesTotal: impayes.reduce((s, v) => s + v.montant, 0),
  }), [virements, impayes]);

  const filtered = useMemo(() =>
    filterMode === 'all' ? virements : virements.filter(v => v.status === filterMode),
    [virements, filterMode]);

  const adherentsSorted = useMemo(() =>
    [...adherents].sort((a, b) => a.nom.localeCompare(b.nom)),
    [adherents]);

  const activeImpayes    = impayes.filter(v => !v.ignored && !v.confirmed);
  const confirmedImpayes = impayes.filter(v => v.confirmed);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">🏦 Pointage Relevé BFC OI</div>
          <div className="page-subtitle" style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            Rapprochement automatique · {selectedYear}
            {detectedMonth && (
              <span style={{ background:'#dbeafe', color:'#1d4ed8', borderRadius:999, padding:'2px 10px', fontSize:12, fontWeight:700 }}>
                Relevé {MOIS_FR[detectedMonth - 1]} {selectedYear}
              </span>
            )}
          </div>
        </div>
        {virements.length > 0 && (
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-outline btn-sm" onClick={handleExport}>⬇️ CSV</button>
            <button className="btn btn-primary btn-sm" onClick={handleValidate}
              disabled={kpi.pointes === 0}>
              ✅ Valider ({kpi.pointes})
            </button>
          </div>
        )}
      </div>

      {error       && <div className="alert alert-danger mb-4"><span>❌</span><div>{error}</div></div>}
      {validationMsg && <div className="alert alert-success mb-4"><span>✅</span><div>{validationMsg}</div></div>}

      {/* KPI Cards */}
      {(virements.length > 0 || impayes.length > 0) && (
        <div className="grid-3 mb-4" style={{ gap:14 }}>
          <div className="stat-card" style={{ borderTopColor:'#1d4ed8' }}>
            <div className="stat-label">Total reçu (relevé)</div>
            <div className="stat-value" style={{ color:'#1d4ed8' }}>{formatMontant(kpi.totalMontant)}</div>
            <div className="stat-sub">{kpi.total} virements · {kpi.validated} synchronisé(s)</div>
          </div>
          <div className="stat-card" style={{ borderTopColor:'#166534' }}>
            <div className="stat-label">Reconnus automatiquement</div>
            <div className="stat-value" style={{ color:'#166534' }}>{kpi.auto}</div>
            <div className="stat-sub">{kpi.pointes} coché(s) à valider</div>
          </div>
          <div className="stat-card" style={{ borderTopColor:'#CC0000' }}>
            <div className="stat-label">Prélèv. rejetés ❌</div>
            <div className="stat-value" style={{ color:'#CC0000' }}>{kpi.impayes}</div>
            <div className="stat-sub">{formatMontant(kpi.impayesTotal)} · {kpi.impayesConfirmed} confirmé(s)</div>
          </div>
        </div>
      )}

      {/* ── BLOC IMPAYES (fond rouge) ── */}
      {activeImpayes.length > 0 && (
        <div className="card mb-4" style={{ border:'2px solid #CC0000', background:'#fff0f0' }}>
          <div className="card-header" style={{ background:'#fff0f0', borderBottom:'1px solid #ffcccc' }}>
            <div className="card-title" style={{ color:'#CC0000' }}>
              ⚠️ Prélèvements rejetés détectés — {activeImpayes.length} cas
            </div>
            <span style={{ fontSize:11, color:'#991b1b' }}>Confirmer pour synchroniser "Défaut paiement" dans le pointage de {MOIS_FR[(relevéMonth)-1]} {selectedYear}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width:70 }}>Date</th>
                  <th>Libellé relevé</th>
                  <th style={{ textAlign:'right', width:100 }}>Montant rejeté</th>
                  <th style={{ minWidth:180 }}>Adhérent associé</th>
                  <th style={{ width:110 }}>Match</th>
                  <th style={{ width:210 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeImpayes.map(imp => {
                  const adhId = imp.manualAdherentId || imp.matchedAdherentId;
                  const adh   = adherents.find(a => a.id === adhId);
                  return (
                    <tr key={imp.id} style={{ background:'#fff5f5' }}>
                      <td style={{ fontSize:12, color:'#6b7280' }}>{imp.date}</td>
                      <td title={imp.ligneOriginale}>
                        <span style={{ fontFamily:'monospace', fontSize:11 }}>{imp.nomBrut}</span>
                      </td>
                      <td style={{ textAlign:'right', fontWeight:800, color:'#CC0000', whiteSpace:'nowrap' }}>
                        {formatMontant(imp.montant)}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                            <AdherentPicker
                              value={imp.manualAdherentId || imp.matchedAdherentId || null}
                              adherents={adherentsSorted}
                              onChange={id => setImpayeAdherent(imp.id, id)}
                              placeholder="🔍 Rechercher…"
                            />
                            {saveAdherent && (
                              <button className="btn btn-sm"
                                style={{ background:'#fff5f5', color:'#CC0000', border:'1px solid #fca5a5', whiteSpace:'nowrap', fontSize:11 }}
                                title="Créer un nouvel adhérent"
                                onClick={() => openCreateModal(imp, true)}>➕</button>
                            )}
                          </div>
                      </td>
                      <td>
                        {imp.status === 'auto'
                          ? <span style={{ background:'#fee2e2', color:'#991b1b', borderRadius:999, padding:'2px 8px', fontSize:10, fontWeight:700 }}>✅ Reconnu</span>
                          : imp.status === 'approx'
                          ? <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:999, padding:'2px 8px', fontSize:10, fontWeight:700 }}>⚠️ Approx.</span>
                          : <span style={{ background:'#f3f4f6', color:'#9ca3af', borderRadius:999, padding:'2px 8px', fontSize:10, fontWeight:700 }}>❌ Inconnu</span>}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          <button
                            style={{ background: adhId ? '#CC0000' : '#e5e7eb', color: adhId ? '#fff' : '#9ca3af',
                              fontSize:11, padding:'4px 10px', borderRadius:6, border:'none', cursor: adhId ? 'pointer' : 'not-allowed' }}
                            disabled={!adhId} onClick={() => confirmImpaye(imp)}>
                            ❌ Confirmer défaut
                          </button>
                          <button className="btn btn-outline btn-xs" style={{ fontSize:10 }}
                            onClick={() => { const n = window.prompt('Note (obligatoire) :'); if (n) ignoreImpaye(imp.id, n); }}>
                            Ignorer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {confirmedImpayes.length > 0 && (
            <div style={{ padding:'8px 14px', fontSize:12, color:'#166534', borderTop:'1px solid #ffcccc', background:'#f0fdf4' }}>
              ✅ {confirmedImpayes.length} rejet(s) confirmé(s) — pointage mensuel synchronisé
            </div>
          )}
        </div>
      )}

      {/* Import zone */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="card-title">📄 Import relevé PDF BFC OI</div>
          <span style={{ fontSize:12, color:'#9ca3af' }}>Virements entrants · Impayés SEPA · 100 % local</span>
        </div>
        <div
          style={{
            border: `2px dashed ${isDrag ? '#CC0000' : '#e5e7eb'}`,
            borderRadius: 10, padding: '28px 20px', textAlign: 'center',
            cursor: processing ? 'default' : 'pointer',
            background: isDrag ? '#fff5f5' : '#fafafa', transition: 'all 0.2s',
          }}
          onDragOver={e => { e.preventDefault(); setIsDrag(true); }}
          onDragLeave={() => setIsDrag(false)}
          onDrop={handleDrop}
          onClick={() => !processing && fileRef.current?.click()}
        >
          {processing ? (
            <div style={{ color: '#CC0000', fontSize: 15, fontWeight: 600 }}>
              ⏳ Extraction et rapprochement en cours…
            </div>
          ) : virements.length > 0 ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>🔄</div>
              Cliquez pour importer un nouveau relevé
            </div>
          ) : (
            <div style={{ color: '#9ca3af' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                Glissez votre relevé BFC (PDF)
              </div>
              <div style={{ fontSize: 12 }}>ou cliquez pour sélectionner le fichier</div>
              <div style={{ fontSize:11, marginTop:8, color:'#bbb' }}>
                "VIRT DE / VIR RECU" pour virements · "IMPAYE PREL / IMP PREL" pour rejets
              </div>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFileChange} />
      </div>

      {/* Results table — virements (crédits) */}
      {virements.length > 0 && (
        <div className="card">
          <div className="card-header" style={{ flexWrap:'wrap', gap:8 }}>
            <div className="card-title">� Virements reçus — cotisations{detectedMonth ? ` — ${MOIS_FR[detectedMonth-1]} ${selectedYear}` : ''}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { key: 'all',    label: 'Tout',         count: kpi.total },
                { key: 'auto',   label: '✅ Reconnus',   count: kpi.auto },
                { key: 'approx', label: '⚠️ À vérifier', count: kpi.approx },
                { key: 'none',   label: '❌ Inconnus',   count: kpi.none },
              ].map(f => (
                <button key={f.key}
                  className={`btn btn-sm ${filterMode === f.key ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setFilterMode(f.key)}
                  style={{ fontSize: 11 }}>
                  {f.label} ({f.count})
                </button>
              ))}
              <span style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 4px' }} />
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                onClick={() => toggleAll(true)}>Tout cocher</button>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                onClick={() => toggleAll(false)}>Tout décocher</button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <div className="empty-state-icon">🔍</div>
              <div className="empty-state-text">Aucun virement dans cette catégorie</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Date</th>
                    <th>Nom relevé / Motifs</th>
                    <th style={{ textAlign: 'right', width: 95 }}>Montant</th>
                    <th style={{ textAlign:'center', width: 60 }}>Score</th>
                    <th style={{ minWidth: 200 }}>Adhérent</th>
                    <th style={{ width: 115 }}>Statut</th>
                    <th style={{ width: 60, textAlign: 'center' }}>Pointé</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => {
                    const adhId = v.manualAdherentId || v.matchedAdherentId;
                    const adh   = adherents.find(a => a.id === adhId);
                    return (
                      <tr key={v.id} style={{ background: v.validated ? '#f0fdf4' : undefined, opacity: v.validated ? 0.87 : 1 }}>
                        <td style={{ color:'#6b7280', fontSize:12, whiteSpace:'nowrap' }}>{v.date}</td>
                        <td title={v.ligneOriginale}>
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                            <span style={{ fontFamily:'monospace', fontSize:12, color:'#1f2937' }}>{v.nomBrut}</span>
                            {v.validated && (
                              <span style={{ background:'#dcfce7', color:'#166534', borderRadius:999, padding:'1px 8px', fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}
                                title={`Synchronisé — pointage ${MOIS_FR[relevéMonth-1]}`}>
                                ✅ Synchronisé
                              </span>
                            )}
                            {v.matchReasons && v.matchReasons.length > 0 && (
                              <div style={{ display:'flex', gap:3, flexWrap:'wrap', marginTop:3 }}>
                                {v.matchReasons.map((r, i) => (
                                  <span key={i} style={{ background:'#f3f4f6', color:'#6b7280', borderRadius:4, padding:'1px 5px', fontSize:9 }}>{r}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign:'right', fontWeight:700, color:'#166534', whiteSpace:'nowrap' }}>
                          {formatMontant(v.montant)}
                        </td>
                        <td style={{ textAlign:'center' }}>
                          {v.matchScore > 0 ? (
                            <span style={{
                              background: v.matchScore >= 75 ? '#dcfce7' : v.matchScore >= 50 ? '#fef3c7' : '#fee2e2',
                              color: v.matchScore >= 75 ? '#166534' : v.matchScore >= 50 ? '#92400e' : '#991b1b',
                              borderRadius: 999, padding: '2px 7px', fontSize: 11, fontWeight: 700
                            }}>{v.matchScore}</span>
                          ) : <span style={{ color:'#d1d5db', fontSize:12 }}>—</span>}
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                            <AdherentPicker
                              value={v.manualAdherentId || v.matchedAdherentId || null}
                              adherents={adherentsSorted}
                              onChange={id => v.validated ? changeValidatedAdherent(v, id) : setManualAdherent(v.id, id)}
                              placeholder="🔍 Rechercher…"
                            />
                            {saveAdherent && (
                              <button
                                className="btn btn-sm"
                                style={{ background:'#f0fdf4', color:'#166534', border:'1px solid #86efac', whiteSpace:'nowrap', fontSize:11 }}
                                title="Créer un nouvel adhérent depuis ce virement"
                                onClick={() => openCreateModal(v)}
                              >➕</button>
                            )}
                          </div>
                        </td>
                        <td>
                          {v.status === 'auto'
                            ? <span style={{ background:'#dcfce7', color:'#166534', borderRadius:999, padding:'2px 10px', fontSize:11, fontWeight:700 }}>✅ Reconnu</span>
                            : v.status === 'approx'
                            ? <span style={{ background:'#fef3c7', color:'#92400e', borderRadius:999, padding:'2px 10px', fontSize:11, fontWeight:700 }}>⚠️ Approx.</span>
                            : <span style={{ background:'#fee2e2', color:'#991b1b', borderRadius:999, padding:'2px 10px', fontSize:11, fontWeight:700 }}>❌ Inconnu</span>}
                        </td>
                        <td style={{ textAlign:'center' }}>
                          {v.validated ? (
                            <button className="btn btn-ghost btn-xs" style={{ fontSize:10, color:'#d97706' }}
                              title={`Annuler la synchronisation avec le pointage de ${MOIS_FR[relevéMonth-1]}`}
                              onClick={() => cancelValidation(v)}>↩ Annuler</button>
                          ) : (
                            <input type="checkbox" checked={v.pointe} disabled={!adhId}
                              title={!adhId ? "Sélectionnez un adhérent d'abord" : ''}
                              style={{ accentColor:'#CC0000', width:16, height:16, cursor:adhId ? 'pointer' : 'not-allowed' }}
                              onChange={e => togglePointe(v.id, e.target.checked)} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display:'flex', gap:8, marginTop:14, justifyContent:'flex-end', padding:'0 4px 4px' }}>
            <button className="btn btn-outline btn-sm" onClick={handleExport}>⬇️ CSV</button>
            <button className="btn btn-primary btn-sm" onClick={handleValidate} disabled={kpi.pointes === 0}>
              ✅ Valider le pointage ({kpi.pointes} sélectionné{kpi.pointes !== 1 ? 's' : ''})
              {detectedMonth ? ` — ${MOIS_FR[detectedMonth-1]} ${selectedYear}` : ''}
            </button>
          </div>
        </div>
      )}

      {/* Modal création rapide adhérent */}
      {createModal && (
        <div className="modal-overlay" onClick={() => setCreateModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">➕ Créer un adhérent depuis ce virement</div>
              <button className="btn-icon" onClick={() => setCreateModal(null)}>✕</button>
            </div>
            <p style={{ fontSize:12, color:'#6b7280', margin:'4px 0 12px' }}>
              Virement : <strong style={{ fontFamily:'monospace' }}>{virements.find(v => v.id === createModal.virementId)?.nomBrut}</strong>
              {' · '}<strong style={{ color:'#166534' }}>{formatMontant(parseFloat(createModal.form.montant)||0)}</strong>
            </p>
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              {['M.','Mme'].map(c => (
                <label key={c} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', padding:'6px 14px', borderRadius:6, border:`1px solid ${createModal.form.civilite===c?'#CC0000':'#ddd'}`, background:createModal.form.civilite===c?'#fff5f5':'#fff', fontWeight:createModal.form.civilite===c?700:400, fontSize:13 }}>
                  <input type="radio" checked={createModal.form.civilite===c} onChange={() => setCreateModal(m => ({ ...m, form: { ...m.form, civilite:c } }))} style={{ accentColor:'#CC0000' }} /> {c}
                </label>
              ))}
            </div>
            <div className="grid-2" style={{ gap:10 }}>
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input className="form-control" value={createModal.form.nom}
                  onChange={e => setCreateModal(m => ({ ...m, form: { ...m.form, nom: e.target.value.toUpperCase() } }))}
                  placeholder="NOM" />
              </div>
              <div className="form-group">
                <label className="form-label">Prénom</label>
                <input className="form-control" value={createModal.form.prenom}
                  onChange={e => setCreateModal(m => ({ ...m, form: { ...m.form, prenom: e.target.value } }))}
                  placeholder="Prénom" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Établissement</label>
                <select className="form-control" value={createModal.form.etablissementId}
                  onChange={e => setCreateModal(m => ({ ...m, form: { ...m.form, etablissementId: e.target.value } }))}>
                  <option value="">— Aucun —</option>
                  {(etablissements||[]).map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Montant (€)</label>
                <input className="form-control" type="number" step="0.01" value={createModal.form.montant}
                  onChange={e => setCreateModal(m => ({ ...m, form: { ...m.form, montant: e.target.value } }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Périodicité</label>
                <select className="form-control" value={createModal.form.periodicite}
                  onChange={e => setCreateModal(m => ({ ...m, form: { ...m.form, periodicite: e.target.value } }))}>
                  {['Mensuelle','Trimestrielle','Semestrielle','Annuelle'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <p style={{ fontSize:11, color:'#9ca3af', margin:'8px 0 0' }}>
              Mode de paiement : Virement · Statut : Actif · La fiche complète sera à remplir dans Adhérents.
            </p>
            <div className="modal-footer" style={{ marginTop:16 }}>
              <button className="btn btn-outline" onClick={() => setCreateModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreateAdherent}
                disabled={!createModal.form.nom.trim()}>
                ➕ Créer et associer au virement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historique */}
      {history.length > 0 && (
        <div className="card mt-4">
          <div className="card-header">
            <div className="card-title">📋 Historique des imports</div>
            <button className="btn btn-ghost btn-xs"
              onClick={() => { const h = []; setHistory(h); saveHist(h); }}>
              Effacer
            </button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date import</th><th>Fichier</th><th>Mois</th>
                  <th style={{ textAlign:'center' }}>Virements</th>
                  <th style={{ textAlign:'center' }}>Impayés</th>
                  <th style={{ textAlign:'center' }}>Reconnus</th>
                  <th style={{ textAlign:'right' }} title="Somme de tous les virements entrants (crédits) détectés dans le relevé PDF — ne correspond pas aux cotisations seules">Crédits relevé ℹ</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td style={{ color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(h.date).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td style={{ fontFamily:'monospace', fontSize:11, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.fichier}</td>
                    <td style={{ fontSize:12, color:'#1d4ed8', fontWeight:600 }}>{h.mois || '—'}</td>
                    <td style={{ textAlign:'center', fontWeight:700 }}>{h.total}</td>
                    <td style={{ textAlign:'center', color:'#CC0000', fontWeight:700 }}>{h.impayes || 0}</td>
                    <td style={{ textAlign:'center' }}>
                      <span style={{ color:'#166534', fontWeight:700 }}>{h.auto}</span>
                      <span style={{ color:'#9ca3af', fontSize:11 }}> / {h.total}</span>
                    </td>
                    <td style={{ textAlign:'right', fontWeight:700, color:'#166534', whiteSpace:'nowrap' }}>{formatMontant(h.montantTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
